import { SubPageHeader } from '@components/SubPageHeader/SubPageHeader';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { CreateCohort } from './CreateCohort';
import { useEffect, useState } from 'react';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { CohortList } from './CohortList';
import { CreateVisit } from './CreateVisit';
import { CohortType } from '@dynamika-shared/types/cohorts';
import { ConfirmationVisits } from './ConfirmationVisits';
import { VisitConfig } from '@dynamika-shared/types/visits';
import { useNavigate } from 'react-router-dom';
import { routesPath } from '@router/routes';
import { VisitsControlSchemaType } from '../uiControlSchemaByStatus/ControlTypes';
import { uiControlSchemas } from '../uiControlSchemaByStatus/schemas';
import { StudyStatusEnum } from '@dynamika-shared/types/status';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { UserRoles } from '@dynamika-shared/types/auth';

export type CohortMode = 'COHORT' | 'NO_COHORT' | 'EDIT_COHORT';
export type VisitMode = 'CREATE_VISIT' | 'EDIT_VISIT';
export type VisitsModalType = 'NO_LABELS' | 'DELETE_VISIT' | 'DELETE_COHORT';

export const ClinicalTrialVisitsPage = () => {

  const isPM = useAuthSlice(s => s?.data?.selectedRole === UserRoles.PROJECT_MANAGER);

  const [clinicalTrial, loading, cohorts, deleteCohort, deleteVisit] =
    useClinicalTrialSlice((state, actions) => [
      state.data,
      state.clinicalTrialActionLoading,
      state.data?.cohorts || [],
      actions.deleteCohortRequest,
      actions.deleteVisitRequest,
    ]);
  const navigate = useNavigate();
  const [cohortMode, setCohortMode] = useState<CohortMode>();
  const [shouldBeExpand, setShouldBeExpand] = useState<string>();
  const [currentCohort, setCurrentCohort] = useState<CohortType>();
  const [currentVisit, setCurrentVisit] = useState<VisitConfig>();
  const [visitMode, setVisitMode] = useState<VisitMode>();
  const [currentModal, setCurrentModal] = useState<VisitsModalType>();
  const [deleteId, setDeleteId] = useState<string>();

  useBreadcrumbs(
    [
      ['Clinical Trials', 'clinicalTrials'],
      [clinicalTrial?.name, 'clinicalTrialGeneralInformation'],
      ['Visits Schedule', 'clinicalTrialVisits'],
    ],
    [clinicalTrial?.id]
  );

  const [uiControlSchema, setUiControlSchema] =
    useState<VisitsControlSchemaType>(
      uiControlSchemas[clinicalTrial?.status ?? StudyStatusEnum.draft].visits
    );

  const confirm = () => {
    if (
      currentModal === 'DELETE_COHORT' &&
      clinicalTrial?.id &&
      currentCohort?.id
    ) {
      deleteCohort({
        clinicalTrialId: clinicalTrial.id,
        cohortId: currentCohort.id,
        onSuccess: () => {
          setCurrentCohort(undefined);
          setCurrentModal(undefined);
        },
      });
    }
    if (currentModal === 'DELETE_VISIT' && clinicalTrial?.id && deleteId) {
      deleteVisit({
        clinicalTrialId: clinicalTrial.id,
        visitId: deleteId,
        onSuccess: () => {
          setDeleteId(undefined);
          setCurrentCohort(undefined);
          setCurrentModal(undefined);
        },
      });
    }
  };

  useEffect(() => {
    if (clinicalTrial?.status) {
      const schema = uiControlSchemas[clinicalTrial?.status].visits;
      setUiControlSchema(schema);
    }
  }, [clinicalTrial?.status]);

  return (
    <div className='relative flex min-h-full w-full grow flex-col'>
      <SubPageHeader
        title='Visits Schedule'
        action={() => setCohortMode('COHORT')}
        showButton={!uiControlSchema.addCohort.hidden && !!cohorts.length && !isPM}
        buttonText='Cohort'
      />
      {cohorts.length ? (
        <CohortList
          cohorts={cohorts}
          uiControlSchema={uiControlSchema}
          shouldBeExpand={shouldBeExpand}
          deleteVisitAction={visitId => {
            setCurrentModal('DELETE_VISIT');
            setDeleteId(visitId);
          }}
          addVisitAction={cohort => {
            if (!clinicalTrial?.labelGroups?.length) {
              setCurrentModal('NO_LABELS');
              return;
            }
            setVisitMode('CREATE_VISIT');
            setCurrentCohort(cohort);
          }}
          editCohortAction={cohort => {
            setCohortMode('EDIT_COHORT');
            setCurrentCohort(cohort);
          }}
          deleteCohortAction={cohort => {
            setCurrentModal('DELETE_COHORT');
            setCurrentCohort(cohort);
          }}
          editVisitAction={(visit, cohort) => {
            setVisitMode('EDIT_VISIT');
            setCurrentVisit(visit);
            setCurrentCohort(cohort);
          }}
        />
      ) : !isPM && (
        <NoContent
          action={() => setCohortMode('COHORT')}
          iconName={'cohorts'}
          title={
            <span className='text-spanishGray'>
              No <b className='text-chineseBlack'>Cohorts</b> here yet! <br />
              Every study must have at least one cohort. In the absence of{' '}
              <br />
              any specified cohorts, a default cohort will be created.
            </span>
          }
        >
          <Button
            className='w-[14.4rem] border-none !bg-blackOlive  !text-1420 text-white hover:!bg-dodgerBlue'
            onClick={() => setCohortMode('NO_COHORT')}
          >
            No Cohorts
          </Button>
          <Button
            className='w-[14.4rem] !bg-white !text-1420  hover:!bg-dodgerBlue'
            onClick={() => setCohortMode('COHORT')}
          >
            Add Cohorts
          </Button>
        </NoContent>
      )}
      {cohortMode && (
        <CreateCohort
          cohortMode={cohortMode}
          numberOfPatient={clinicalTrial?.patientHypothesis}
          close={() => {
            setCohortMode(undefined);
            setCurrentCohort(undefined);
          }}
          cohort={currentCohort}
        />
      )}
      {visitMode && (
        <CreateVisit
          visitMode={visitMode}
          cohorts={cohorts}
          currentCohort={currentCohort}
          currentVisit={currentVisit}
          close={() => {
            setVisitMode(undefined);
            setCurrentCohort(undefined);
            setCurrentVisit(undefined);
          }}
          setShouldBeExpand={setShouldBeExpand}
        />
      )}
      {currentModal && (
        <ConfirmationVisits
          modalType={currentModal}
          onCancel={() => setCurrentModal(undefined)}
          onOk={confirm}
          loading={loading}
          navigate={() =>
            navigate(
              routesPath.clinicalTrialImagingLabels.replace(
                ':id',
                clinicalTrial?.id as string
              )
            )
          }
        />
      )}
    </div>
  );
};
